// Composes the menu from the entries the feature modules declare, then sorts
// and gates it.
//
// MIGRATION STATE (Phase 1): every entry below is still "legacy" — it carries a
// `legacyDomId` and no `activate`, so MenuHost renders a button with the id the
// module already looks up and the module's own listener keeps handling the
// click. This is what lets index.html lose its 19 hardcoded buttons in a single
// commit without touching a single feature module.
//
// Phase 3 moves these declarations into the modules themselves (install() starts
// returning a MenuItem) one at a time; each such PR drops a legacyDomId and adds
// an activate.

import { audienceAllows } from './audience.ts';
import {
  hasCopresence,
  hasDeviceOrientation,
  hasDollhouseToggle,
  hasFloorplanGeometry,
  hasRoomPanelContent,
  hasVoiceInput,
  hasSpeechSynthesis,
} from './capabilities.ts';
import type { MenuContext, MenuItem } from './types.ts';

/**
 * The buyer-facing and operator-facing entries that still live in App and the
 * feature modules. Ordered here only for readability — `priority` is what sorts.
 */
export function legacyMenuItems(): MenuItem[] {
  return [
    // ── Primary: what a buyer reaches for first ────────────────────────────
    {
      id: 'floorplan',
      legacyDomId: 'floorplan-toggle',
      // NOT hasFloorplanGeometry — this panel has a fallback the dollhouse does
      // not. With no polygons it still renders the room list, so gating on
      // geometry removed the only control for a panel that kept drawing. See
      // hasRoomPanelContent.
      isAvailable: (ctx) => hasRoomPanelContent(ctx.rooms),
      labelKey: 'menu.floorplan.label',
      ariaKey: 'menu.floorplan.aria',
      titleKey: 'menu.floorplan.title',
      // The panel draws a plan OR a room list; say which one this tour gets.
      labelsFor: (ctx) =>
        hasFloorplanGeometry(ctx.rooms)
          ? {
              labelKey: 'menu.floorplan.label',
              ariaKey: 'menu.floorplan.aria',
              titleKey: 'menu.floorplan.title',
            }
          : {
              labelKey: 'menu.rooms.label',
              ariaKey: 'menu.rooms.aria',
              titleKey: 'menu.rooms.title',
            },
      icon: '🗺️',
      group: 'navegar',
      tier: 'primary',
      priority: 10,
      audience: 'buyer',
      surfaces: 'dom',
    },
    {
      // Sits immediately after 🗺 on purpose: the two are the same job — where
      // am I, and how do the rooms fit together — and a buyer reaches for them
      // together. Adjacency has to be bought with `tier: 'primary'`, not with
      // `priority` alone: MenuHost sorts by tier FIRST and only breaks ties on
      // priority, so a 'secondary' entry lands after every primary no matter
      // how low its number.
      id: 'dollhouse',
      legacyDomId: 'dollhouse-toggle',
      isAvailable: (ctx) => hasDollhouseToggle(ctx.rooms),
      labelKey: 'menu.dollhouse.label',
      ariaKey: 'menu.dollhouse.aria',
      titleKey: 'menu.dollhouse.title',
      icon: '🏠',
      group: 'navegar',
      tier: 'primary',
      priority: 15,
      audience: 'buyer',
      surfaces: 'dom',
    },
    {
      id: 'sun',
      legacyDomId: 'sun-toggle',
      labelKey: 'menu.sun.label',
      ariaKey: 'menu.sun.aria',
      titleKey: 'menu.sun.title',
      icon: '☀️',
      group: 'ver',
      tier: 'primary',
      priority: 20,
      audience: 'buyer',
      surfaces: 'both',
      vr: { kind: 'submenu', submenu: 'time' },
    },
    {
      id: 'share',
      legacyDomId: 'share-button',
      labelKey: 'menu.share.label',
      ariaKey: 'menu.share.aria',
      titleKey: 'menu.share.title',
      icon: '🔗',
      group: 'compartilhar',
      tier: 'primary',
      priority: 30,
      audience: 'buyer',
      surfaces: 'dom',
    },
    {
      id: 'audio',
      legacyDomId: 'audio-button',
      labelKey: 'menu.audio.label',
      ariaKey: 'menu.audio.aria',
      ariaPressedKey: 'menu.audio.ariaOff',
      icon: '🔇',
      group: 'acessibilidade',
      tier: 'primary',
      priority: 40,
      audience: 'buyer',
      surfaces: 'both',
      vr: { kind: 'toggle' },
    },

    // ── Secondary: promoted into the rail when there is room ───────────────
    {
      id: 'narrator',
      legacyDomId: 'narrator-button',
      labelKey: 'menu.narrator.label',
      ariaKey: 'menu.narrator.aria',
      ariaPressedKey: 'menu.narrator.ariaOff',
      titleKey: 'menu.narrator.title',
      icon: '🔈',
      group: 'acessibilidade',
      tier: 'secondary',
      priority: 60,
      audience: 'buyer',
      surfaces: 'both',
      isAvailable: hasSpeechSynthesis,
      vr: { kind: 'toggle' },
    },
    {
      id: 'autotour',
      legacyDomId: 'auto-tour-button',
      labelKey: 'menu.autotour.label',
      ariaKey: 'menu.autotour.aria',
      titleKey: 'menu.autotour.title',
      icon: '🎬',
      group: 'navegar',
      tier: 'secondary',
      priority: 70,
      audience: 'buyer',
      // 'both', unlike the other panels: the cinematic tour is camera motion,
      // fade transitions and narration — all of which happen in the scene and
      // are therefore the only menu entry besides sound and narration that a
      // headset visitor can actually experience. It is also the single best
      // thing to be able to say in VR ("start the tour") and let go.
      surfaces: 'both',
      vr: { kind: 'toggle' },
    },
    {
      id: 'chat',
      legacyDomId: 'chat-button',
      labelKey: 'menu.chat.label',
      ariaKey: 'menu.chat.aria',
      titleKey: 'menu.chat.title',
      icon: '💬',
      group: 'navegar',
      tier: 'secondary',
      priority: 80,
      audience: 'buyer',
      surfaces: 'dom',
    },
    {
      id: 'qr',
      legacyDomId: 'qr-button',
      labelKey: 'menu.qr.label',
      ariaKey: 'menu.qr.aria',
      titleKey: 'menu.qr.title',
      icon: '📱',
      group: 'compartilhar',
      tier: 'secondary',
      priority: 90,
      audience: 'buyer',
      surfaces: 'dom',
    },
    {
      id: 'whatsapp',
      legacyDomId: 'whatsapp-button',
      labelKey: 'menu.whatsapp.label',
      ariaKey: 'menu.whatsapp.aria',
      titleKey: 'menu.whatsapp.title',
      icon: '📲',
      group: 'compartilhar',
      tier: 'secondary',
      priority: 100,
      audience: 'buyer',
      surfaces: 'dom',
    },
    {
      id: 'saved-session',
      legacyDomId: 'saved-session-button',
      labelKey: 'menu.savedSession.label',
      ariaKey: 'menu.savedSession.aria',
      titleKey: 'menu.savedSession.title',
      icon: '💾',
      group: 'compartilhar',
      tier: 'secondary',
      priority: 110,
      audience: 'buyer',
      surfaces: 'dom',
    },
    {
      id: 'gallery',
      legacyDomId: 'gallery-button',
      labelKey: 'menu.gallery.label',
      ariaKey: 'menu.gallery.aria',
      titleKey: 'menu.gallery.title',
      icon: '🖼️',
      group: 'ver',
      tier: 'secondary',
      priority: 120,
      audience: 'buyer',
      surfaces: 'dom',
    },

    // ── Overflow: never competes for a rail slot ───────────────────────────
    {
      id: 'voice',
      legacyDomId: 'voice-command-button',
      labelKey: 'menu.voice.label',
      ariaKey: 'menu.voice.aria',
      titleKey: 'menu.voice.title',
      icon: '🗣️',
      group: 'acessibilidade',
      tier: 'overflow',
      priority: 130,
      audience: 'buyer',
      surfaces: 'dom',
      isAvailable: hasVoiceInput,
    },
    {
      id: 'tilt',
      legacyDomId: 'tilt-button',
      labelKey: 'menu.tilt.label',
      ariaKey: 'menu.tilt.aria',
      titleKey: 'menu.tilt.title',
      icon: '🔭',
      group: 'acessibilidade',
      tier: 'overflow',
      priority: 140,
      audience: 'buyer',
      surfaces: 'dom',
      isAvailable: hasDeviceOrientation,
    },

    // ── Operator tools — behind ?operator=1 ────────────────────────────────
    // These were sharing a row with "share" and "sound" on every public tour.
    {
      id: 'measure',
      legacyDomId: 'measure-button',
      labelKey: 'menu.measure.label',
      ariaKey: 'menu.measure.aria',
      icon: '📏',
      group: 'ferramentas',
      tier: 'secondary',
      priority: 200,
      audience: 'operator',
      surfaces: 'dom',
    },
    {
      id: 'note',
      legacyDomId: 'note-button',
      labelKey: 'menu.note.label',
      ariaKey: 'menu.note.aria',
      icon: '📝',
      group: 'ferramentas',
      tier: 'secondary',
      priority: 210,
      audience: 'operator',
      surfaces: 'dom',
    },
    {
      id: 'report',
      legacyDomId: 'report-button',
      labelKey: 'menu.report.label',
      ariaKey: 'menu.report.aria',
      icon: '📄',
      group: 'ferramentas',
      tier: 'overflow',
      priority: 220,
      audience: 'operator',
      surfaces: 'dom',
    },
    {
      id: 'copresence',
      legacyDomId: 'copresence-button',
      labelKey: 'menu.copresence.label',
      ariaKey: 'menu.copresence.aria',
      icon: '👥',
      group: 'ferramentas',
      tier: 'overflow',
      priority: 230,
      audience: 'operator',
      surfaces: 'dom',
      isAvailable: hasCopresence,
    },
    {
      id: 'follow',
      legacyDomId: 'follow-button',
      labelKey: 'menu.follow.label',
      ariaKey: 'menu.follow.aria',
      icon: '🎯',
      group: 'ferramentas',
      tier: 'overflow',
      priority: 240,
      audience: 'operator',
      surfaces: 'dom',
      isAvailable: hasCopresence,
      // Copresence un-hides this by removing .hidden once a peer joins; it
      // starts hidden, exactly as the hardcoded markup did.
      startsHidden: true,
    },
  ];
}

/**
 * Operator tools that never had a button — only a bare keypress, which is
 * unreachable on a phone and in a headset. Unlike everything above these carry
 * `activate`, so they also own their shortcut: the modules' own keydown
 * listeners were removed in the same change, because two listeners on one key
 * toggle twice and cancel out.
 */
export interface OperatorMenuDeps {
  /** The session-insights overlay, previously H-only (dashboard.ts). */
  toggleDashboard(): void;
  /** The yaw/pitch readout used to place hotspots, previously D-only (debugLook.ts). */
  toggleDebugLook(): void;
}

export function operatorMenuItems(deps: OperatorMenuDeps): MenuItem[] {
  return [
    {
      id: 'dashboard',
      labelKey: 'menu.dashboard.label',
      ariaKey: 'menu.dashboard.aria',
      icon: '📊',
      group: 'ferramentas',
      tier: 'overflow',
      priority: 250,
      audience: 'operator',
      surfaces: 'dom',
      shortcut: 'h',
      activate: () => deps.toggleDashboard(),
    },
    {
      id: 'debug',
      labelKey: 'menu.debug.label',
      ariaKey: 'menu.debug.aria',
      icon: '🧭',
      group: 'ferramentas',
      tier: 'overflow',
      priority: 260,
      audience: 'operator',
      surfaces: 'dom',
      shortcut: 'd',
      activate: () => deps.toggleDebugLook(),
    },
  ];
}

/** Sorts, then drops anything this audience or this browser cannot use. */
export function buildMenu(items: (MenuItem | null | undefined)[], ctx: MenuContext): MenuItem[] {
  const seen = new Set<string>();
  const out: MenuItem[] = [];

  for (const item of items) {
    if (!item) continue; // a module returning null = capability absent
    if (seen.has(item.id)) {
      console.warn(`[menu] duplicate entry id "${item.id}" ignored`);
      continue;
    }
    seen.add(item.id);
    if (!audienceAllows(item.audience, ctx.audience)) continue;
    if (item.isAvailable && !item.isAvailable(ctx)) continue;
    // Resolve content-dependent labels here so no renderer has to. Undefined
    // aria/title in the override must not erase the entry's static ones.
    if (item.labelsFor) {
      const resolved = item.labelsFor(ctx);
      out.push({
        ...item,
        labelKey: resolved.labelKey,
        ariaKey: resolved.ariaKey ?? item.ariaKey,
        titleKey: resolved.titleKey ?? item.titleKey,
      });
      continue;
    }
    out.push(item);
  }

  return out.sort((a, b) => a.priority - b.priority || a.id.localeCompare(b.id));
}
